import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
} from '@mui/material';
import {
  Store,
  QrCode,
  Discount,
  People,
  TrendingUp,
  Verified,
  Support,
  Analytics,
  LocationOn,
  Speed,
  Security,
  Smartphone,
  CalendarToday,
  Settings,
  LocalOffer,
  Celebration,
  BarChart,
  HeadsetMic,
  Phone,
  School,
  CheckCircle,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import BackToTop from '../components/BackToTop';
import SEO from '../components/SEO';

const BusinessPage = () => {

  const howPartnershipWorks = [
    'Register your business with 10D Stores',
    'Set your discount rates and terms for 10D members',
    'Get listed in our business directory with your location and offerings',
    '10D members discover your business and visit your store',
    'Members scan their QR code to verify membership',
    'Apply the agreed discount and boost your sales',
  ];

  const partnershipStats = [
    { number: '1000+', label: 'Partner Businesses' },
    { number: '50K+', label: 'Active 10D Members' },
    { number: '25%', label: 'Average Sales Increase' },
    { number: '₹0', label: 'Partnership Fees' },
  ];

  return (
    <Box>
      <SEO
        title="Partner with Us"
        description="Register your business with 10D Stores to reach thousands of verified members, increase foot traffic, and boost sales."
        canonical="https://10dstores.in/business"
      />
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: 'relative',
          color: 'white',
          py: 16,
          textAlign: 'center',
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: -2,
          }}
        >
          <source src={require('../assets/videos/business/Jumbotron_Video_for_Tech_Retailer.mp4')} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: -1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '4.5rem',
              lineHeight: 1.1,
              color: 'white',
              mb: 6,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              '@media (max-width: 768px)': {
                fontSize: '3rem',
              },
              '@media (max-width: 480px)': {
                fontSize: '2.5rem',
              }
            }}
          >
            Why Partner with 10D for Your Business?
          </Typography>
          <Typography
            variant="h4"
            component="p"
            gutterBottom
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '1.75rem',
              color: 'white',
              maxWidth: '700px',
              mb: 6,
              mx: 'auto',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              lineHeight: 1.4,
              minHeight: '3rem'
            }}
          >
            <Typewriter
              onInit={(typewriter) => {
                typewriter
                  .pauseFor(2000)
                  .typeString('Connect with thousands of verified 10D members.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Increase foot traffic and boost your sales.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Simple partnership model with no delivery hassles.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Focus on what you do best - serving customers.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Join hundreds of businesses already thriving.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(1000)
                  .start();
              }}
              options={{
                loop: true,
                delay: 50,
                cursor: '|',
                cursorClassName: 'typewriter-cursor',
              }}
            />
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              className="cta-button"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '1.125rem',
                fontWeight: 700,
                px: '2rem',
                py: '1rem',
                borderRadius: '9999px',
                border: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'white',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                },
              }}
            >
              Become a Partner
            </Button>
            <Button
              variant="outlined"
              size="large"
              className="cta-button"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                backgroundColor: 'transparent',
                borderColor: 'white',
                borderWidth: '2px',
                color: 'white',
                fontSize: '1.125rem',
                fontWeight: 700,
                px: '2rem',
                py: '1rem',
                borderRadius: '9999px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'white',
                  color: 'black',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Grid container spacing={4}>
          {partnershipStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#e3662d', mb: 2 }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ color: '#2d2d2d', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How Partnership Works */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            How 10D Partnership Works
          </Typography>
          <Grid container spacing={4}>
            {howPartnershipWorks.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' },
                  }}
                  elevation={2}
                >
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={index + 1}
                      color="primary"
                      sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2 }}
                    />
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {step}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits of Partnering with 10D Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Benefits of Partnering with 10D
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Connect with thousands of verified 10D members actively looking for local businesses.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <People sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Verified Members
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  All members are verified and quality-checked by our team
                </Typography>
                <Chip label="50K+ Members" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <TrendingUp sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Active Shoppers
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Members actively looking for local business discounts
                </Typography>
                <Chip label="Daily Visits" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocationOn sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Local Focus
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Members prefer businesses in their neighborhood
                </Typography>
                <Chip label="Nearby Search" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Verified sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Quality Customers
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Members who value quality and local businesses
                </Typography>
                <Chip label="Premium Users" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Flexible Discount Control Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Flexible Discount Control
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Set your own discount rates and terms for 10D members.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Discount sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Custom Rates
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Set any discount percentage that works for your business
                </Typography>
                <Chip label="5-50% OFF" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <CalendarToday sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Flexible Terms
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Choose when and how long your discounts are valid
                </Typography>
                <Chip label="Daily/Weekly" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Settings sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Easy Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Update discounts anytime through your dashboard
                </Typography>
                <Chip label="Real-time" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Analytics sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Performance Tracking
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Monitor which discounts work best for your business
                </Typography>
                <Chip label="Smart Data" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Simple Verification System Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Simple Verification System
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Easy QR code scanning to verify 10D membership and apply discounts.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <QrCode sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  One Scan
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Single QR code scan provides instant verification
                </Typography>
                <Chip label="Instant Access" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Speed sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Lightning Fast
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Get verification in seconds, no waiting needed
                </Typography>
                <Chip label="Seconds Only" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Security sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Secure Process
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Each QR code is unique and prevents fraud
                </Typography>
                <Chip label="100% Secure" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Smartphone sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Mobile Friendly
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Works with any smartphone or tablet
                </Typography>
                <Chip label="Mobile Ready" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Increased Foot Traffic Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Increased Foot Traffic
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Drive more customers to your physical store with exclusive offers.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <TrendingUp sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Customer Growth
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Attract new customers who discover your business
                </Typography>
                <Chip label="25% Increase" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocalOffer sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Exclusive Offers
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Create offers that regular customers can't access
                </Typography>
                <Chip label="Members Only" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Store sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Store Discovery
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Get discovered by customers searching for local deals
                </Typography>
                <Chip label="Local Search" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Celebration sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Repeat Visits
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Build loyalty with customers who return for more deals
                </Typography>
                <Chip label="Customer Loyalty" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Customer Insights Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Customer Insights
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Get data on 10D member visits and discount usage.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Analytics sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Visit Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Track how many 10D members visit your store
                </Typography>
                <Chip label="Real-time Data" color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <TrendingUp sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Performance Metrics
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Monitor which discounts work best for your business
                </Typography>
                <Chip label="Smart Insights" color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <People sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Customer Behavior
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Understand when and how customers use your discounts
                </Typography>
                <Chip label="Behavior Analysis" color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <BarChart sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Growth Reports
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Monthly reports on your 10D partnership growth
                </Typography>
                <Chip label="Monthly Reports" color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Dedicated Support Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Dedicated Support
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            24/7 support to help you maximize your 10D partnership.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Support sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  24/7 Support
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Round-the-clock support for all your partnership needs
                </Typography>
                <Chip label="Always Available" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <HeadsetMic sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Live Chat
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Instant chat support for quick questions and issues
                </Typography>
                <Chip label="Instant Help" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Phone sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Phone Support
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Direct phone support for complex partnership issues
                </Typography>
                <Chip label="Direct Contact" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <School sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Training & Resources
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Learn how to maximize your 10D partnership benefits
                </Typography>
                <Chip label="Free Training" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Partnership Tiers Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Partnership Tiers
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Choose the partnership level that best fits your business needs and growth goals.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    Basic Partner
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Perfect for small businesses starting with 10D
                  </Typography>
                </Box>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Business listing in 10D directory" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Basic discount management" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="QR code verification system" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Customer support" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Monthly performance reports" />
                  </ListItem>
                </List>

                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #eeeeee', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    <strong>Requirements:</strong> Valid business license and physical store
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  component={Link}
                  to="/register"
                  sx={{ borderColor: '#e3662d', color: '#e3662d', '&:hover': { backgroundColor: '#e3662d', color: 'white' } }}
                >
                  Apply Now
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '2px solid #e3662d', position: 'relative', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Chip
                  label="Most Popular"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 'bold',
                  }}
                />

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    Premium Partner
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Enhanced features for growing businesses
                  </Typography>
                </Box>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="All Basic Partner features" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Featured business listing" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Advanced analytics dashboard" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Priority customer support" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Marketing support materials" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Exclusive 10D events access" />
                  </ListItem>
                </List>

                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #eeeeee', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    <strong>Requirements:</strong> 6+ months as Basic Partner, 100+ 10D member visits
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to="/register"
                  sx={{ backgroundColor: '#e3662d', '&:hover': { backgroundColor: '#d55a1f' } }}
                >
                  Apply Now
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    Elite Partner
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Top-tier partnership for established businesses
                  </Typography>
                </Box>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="All Premium Partner features" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="VIP business listing" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Custom discount campaigns" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Dedicated account manager" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Business growth consultation" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: '#5a9b9a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="10D community leadership role" />
                  </ListItem>
                </List>

                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #eeeeee', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    <strong>Requirements:</strong> 12+ months as Premium Partner, 500+ 10D member visits
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  component={Link}
                  to="/register"
                  sx={{ borderColor: '#e3662d', color: '#e3662d', '&:hover': { backgroundColor: '#e3662d', color: 'white' } }}
                >
                  Apply Now
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f17a4a 0%, #f8a17a 100%)',
        py: 12,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(241, 122, 74, 0.3)',
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 6, fontSize: '3rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Start Your 10D Partnership Today
            </Typography>
            <Typography variant="h5" sx={{ mb: 8, fontSize: '1.5rem', lineHeight: 1.6, maxWidth: 800, mx: 'auto', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
              Don't let your competitors get ahead. Partner with 10D and start attracting more customers to your store.
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#5a9b9a',
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 700,
                px: 10,
                py: 3,
                borderRadius: '16px',
                border: '2px solid #e6b8a2',
                boxShadow: '0 8px 32px rgba(90,155,154,0.4), 0 4px 16px rgba(230, 184, 162, 0.3)',
                '&:hover': {
                  backgroundColor: '#e3662d',
                  borderColor: '#f5f5f5',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 48px rgba(227, 102, 45, 0.6), 0 8px 32px rgba(230, 184, 162, 0.4)'
                },
                transition: 'all 0.3s ease',
              }}
            >
              Become a Partner
            </Button>
          </Box>
        </Container>
      </Box>

      <BackToTop />
    </Box>
  );
};

export default BusinessPage;
